/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-11-16 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Card,
  DataTable,
  SkeletonDisplayText,
  Pagination,
  Icon,
  Page,
  Layout,
  Button,
  Stack
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { CaretDownMinor, CaretUpMinor, MobileBackArrowMajor } from '@shopify/polaris-icons';
import MainMenu from 'src/components/containers/main-menu';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import { getShop, formatNumberByLocale } from 'src/helpers';
import { getStoreDate, getStoreDateBefore } from 'src/helpers/analytics-helpers';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import {
  QUERY_EXECUTION_KEY,
  TOP_ITEM_IN_DETAIL,
  NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
  MAP_SORT_VALUE,
  SORT_VALUE
} from 'src/constants/analytics';
import noProductImage from 'src/assets/images/product/no-product-image.png';
import { optimizeImage } from 'src/helpers';
import DidYouKnow from 'src/components/views/analytics-v2/did-you-know-search-term-detail';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import TitleHelp from 'src/components/views/common/title-help';
import { URL } from 'src/constants/links';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import WarningMetricIncompatible from 'src/components/views/analytics-v2/warning-metric-incompatible';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {
  perPage: number,
  page: number,
  hasNext: boolean,
  hasPrevious: boolean,
  rows: any[],
  data: any[],
  totalPage: number,
  directionSort: string,
  youtubeModal: boolean
};

const fromDateStateName = 'searchAnalyticsFromDate';
const toDateStateName = 'searchAnalyticsToDate';
const compareStateName = 'searchAnalyticsCompare';

const orderHeaders = [
  '',
  'productName',
  'totalSales',
  'totalOrders',
  'conversionRate',
  'clickCount'
];

class SearchAnalyticsSearchTermDetail extends Component<ComposedProps, State> {
  state = {
    perPage: 10,
    hasPrevious: false,
    hasNext: false,
    rows: [],
    data: [],
    page: 1,
    totalPage: 0,
    directionSort: 'desc',
    youtubeModal: false
  };
  templateKey = 'new-search-analytics';

  componentDidMount() {
    const { changeValue, searchTermDetails, match, getThemes } = this.props;
    const { perPage } = this.state;
    getThemes();

    const hasNext = _.size(searchTermDetails) > perPage;
    this.setState({ rows: searchTermDetails, hasNext });

    // Initial from date, to date, compare
    const today = getStoreDate(new Date());
    const yesterday = getStoreDateBefore(today);
    let fromDate = yesterday,
      toDate = yesterday,
      compare = false;
    // Get data from local storage
    try {
      const fromDateLocalStorage = localStorage.getItem(
        `${getShop()}-${this.templateKey}-fromDate`
      );
      const toDateLocalStorage = localStorage.getItem(`${getShop()}-${this.templateKey}-toDate`);
      const compareLocalStorage = localStorage.getItem(`${getShop()}-${this.templateKey}-compare`);

      fromDate = fromDateLocalStorage ? new Date(fromDateLocalStorage) : fromDate;
      toDate = toDateLocalStorage ? new Date(toDateLocalStorage) : fromDate;
      compare = compareLocalStorage === 'true';
    } catch (error) {}

    changeValue(fromDateStateName, fromDate);
    changeValue(toDateStateName, toDate);
    changeValue(compareStateName, compare);
    changeValue('pathname', 'search-term-detail');

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.handleLeavePage);

    if (fromDate && toDate) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);
      this.getTopSearchTermDetail(
        encodeURIComponent(match.params.searchTerm),
        fromDateUTC,
        toDateUTC,
        compare
      );
    }
  }

  componentWillReceiveProps(nextProps) {
    const { searchTermDetail: nextSearchTermDetail } = nextProps;

    if (!_.isEmpty(nextSearchTermDetail)) {
      const newTotalPage = Math.ceil(
        nextSearchTermDetail.length / NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      );
      this.setState({
        totalPage: newTotalPage
      });
    }

    if (!_.isEmpty(nextSearchTermDetail)) {
      this.paginateData(this.state.page, nextSearchTermDetail);
    }

    // Update execution ids
    this.processExecutionId_SearchTermDetail(this.props, nextProps);
  }

  componentWillUnmount() {
    this.handleLeavePage();

    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.handleLeavePage);
  }

  handleLeavePage = () => {
    // Insert data to local storage
    try {
      const { fromDate, toDate, compare = false } = this.props;
      localStorage.setItem(`${getShop()}-${this.templateKey}-fromDate`, fromDate.toISOString());
      localStorage.setItem(`${getShop()}-${this.templateKey}-toDate`, toDate.toISOString());
      localStorage.setItem(`${getShop()}-${this.templateKey}-compare`, compare.toString());
    } catch (error) {}
  };

  paginateData = (page, data) => {
    this.setState({
      data: data.slice(
        (page - 1) * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
        page * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      )
    });
  };

  buildOptions = () => {
    const { searchTermDetailLoading } = this.props;
    const { data } = this.state;
    const result = [];
    if (searchTermDetailLoading) {
      [...Array(5)].forEach(() =>
        result.push([
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />
        ])
      );
    } else {
      data.forEach(item => {
        const { productName, totalSales, totalOrders, conversionRate, clickCount, productImages } =
          item;
        const firstImageIndex = !_.isEmpty(productImages) ? Object.keys(productImages)[0] : null;
        let image = !firstImageIndex
          ? noProductImage
          : optimizeImage(productImages[firstImageIndex], '40x40');
        result.push([
          <div className={`product-image ${_.isEmpty(productImages) ? 'no-image' : ''}`}>
            <img src={image} alt={productName} />
          </div>,
          <div>{productName}</div>,
          <div className="cell-value-table">{formatNumberByLocale(totalSales)}</div>,
          <div className="cell-value-table">{formatNumberByLocale(totalOrders)}</div>,
          <div className="cell-value-table">{conversionRate}%</div>,
          <div className="cell-value-table">{formatNumberByLocale(clickCount)}</div>
        ]);
      });
    }
    return result;
  };

  handleSort = (headingIndex: number, direction: string) => {
    const { sortData } = this.props;
    this.setState({
      directionSort: MAP_SORT_VALUE[direction]
    });
    sortData('topSearchTermDetail', orderHeaders[headingIndex], MAP_SORT_VALUE[direction], '');
  };

  nextPage = () => {
    const { page, totalPage } = this.state;
    const { searchTermDetail } = this.props;
    if (page === totalPage) {
      return;
    }

    this.paginateData(page + 1, searchTermDetail);
    this.setState({
      page: page + 1
    });
  };

  previousPage = () => {
    const { page } = this.state;
    const { searchTermDetail } = this.props;
    if (page === 1) {
      return;
    }

    this.paginateData(page - 1, searchTermDetail);
    this.setState({
      page: page - 1
    });
  };

  getTopSearchTermDetail = (searchTerm, fromDate, toDate, compare) => {
    const { executionIds, match } = this.props;

    // TODO: remove this line when there's any update later
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate,
      toDate,
      compare,
      searchTerm: match.params.searchTerm
    });
    const queryExecutionKey = QUERY_EXECUTION_KEY.SEARCH_TERM_DETAIL;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getTopSearchTermDetail(
      searchTerm,
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  processExecutionId_SearchTermDetail = (props, nextProps) => {
    const { searchTermDetail_QueryExecutionId, match } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      // compare: nextCompare, // TODO: remove this line when there's any update later
      searchTermDetail_QueryExecutionId: searchTermDetail_QueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false,
      searchTerm: match.params.searchTerm
    });

    // Update execution id
    if (
      !_.isEmpty(searchTermDetail_QueryExecutionIdNext) &&
      searchTermDetail_QueryExecutionId !== searchTermDetail_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.SEARCH_TERM_DETAIL,
        executionId: searchTermDetail_QueryExecutionIdNext
      });
    }
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  render() {
    const { i18n, searchTermDetail, searchTermDetailLoading, match } = this.props;
    const { page, directionSort, totalPage, youtubeModal } = this.state;
    const options = this.buildOptions();
    const arrowSort = directionSort === SORT_VALUE.ASCENDING ? CaretUpMinor : CaretDownMinor;

    return (
      <div className="BC-Page BC-Page__SearchAnalytics" onScroll={this.handleScroll}>
        <MainMenu menuId="menu-analytics-search" />
        <Page
          title={
            <Stack>
              <div className="BC-Page__Search_ActionSearchTermDetail">
                <Button onClick={() => history.push(routes.analytics.searchesWithClick())}>
                  <Icon source={MobileBackArrowMajor} />
                </Button>
                <span
                  variant="heading4xl"
                  as="h1"
                  className="BC-Page__Search_ActionSearchTermDetail__Title">
                  {i18n.translate('AdminHTML.Analytics.SearchAnalytics.searchTermDetail_cardTitle')}
                </span>
              </div>
            </Stack>
          }
          primaryAction={{
            content: i18n.translate('AdminHTML.Analytics.SearchAnalytics.add_merchandising_rule'),
            onAction: () => {
              history.push(routes.filterTree.new());
            }
          }}>
          <TitleHelp
            i18n={i18n}
            hrefUserManual={URL['Link_V2_Analytics_Search_ManualGuide']}
            style={{
              marginBottom: '40px',
              marginLeft: '49px',
              zIndex: '1',
              position: 'relative',
              marginTop: '-19px'
            }}
          />
          <Layout>
            <Layout.Section>
              <WarningMetricIncompatible marginTop="0px" />
            </Layout.Section>
            <Layout.Section>
              <div className="BC-Page BC-Page__SearchAnalytics__SearchTermDetail__DidYouKnow">
                <DidYouKnow />
              </div>
            </Layout.Section>
            <Layout.Section>
              <div className="BC-Page BC-Page__SearchAnalytics__SearchTermDetail">
                <div>
                  <Card title={match.params.searchTerm} sectioned>
                    <DataTable
                      columnContentTypes={[
                        'text',
                        'text',
                        'numeric',
                        'numeric',
                        'numeric',
                        'numeric'
                      ]}
                      sortable={[false, true, true, true, true, true]}
                      onSort={this.handleSort}
                      defaultSortDirection="descending"
                      initialSortColumnIndex={2}
                      footerContent={
                        _.isEmpty(searchTermDetail) && !searchTermDetailLoading
                          ? i18n.translate(
                              'AdminHTML.Analytics.SearchAnalytics.searchesTerms_noResult',
                              { count: 0 }
                            )
                          : ''
                      }
                      headings={[
                        <div></div>,
                        <div className="header-with-sortable">
                          {i18n.translate(
                            'AdminHTML.Analytics.SearchAnalytics.searchesTerms_productHeader'
                          )}
                          <span className="Polaris-DataTable__Icon--custom">
                            <Icon source={arrowSort} color="base" />
                          </span>
                        </div>,
                        <div className="header-with-sortable">
                          {i18n.translate(
                            'AdminHTML.Analytics.SearchAnalytics.searchesTerms_revenueHeader'
                          )}
                          <span className="Polaris-DataTable__Icon--custom">
                            <Icon source={arrowSort} color="base" />
                          </span>
                        </div>,
                        <div className="header-with-sortable">
                          {i18n.translate(
                            'AdminHTML.Analytics.SearchAnalytics.searchesTerms_orderCountHeader'
                          )}
                          <span className="Polaris-DataTable__Icon--custom">
                            <Icon source={arrowSort} color="base" />
                          </span>
                        </div>,
                        <div className="header-with-sortable">
                          {i18n.translate(
                            'AdminHTML.Analytics.SearchAnalytics.searchesTerms_conversionRateHeader'
                          )}
                          <span className="Polaris-DataTable__Icon--custom">
                            <Icon source={arrowSort} color="base" />
                          </span>
                        </div>,
                        <div className="header-with-sortable">
                          {i18n.translate(
                            'AdminHTML.Analytics.SearchAnalytics.searchesTerms_clickCountHeader'
                          )}
                          <span className="Polaris-DataTable__Icon--custom">
                            <Icon source={arrowSort} color="base" />
                          </span>
                        </div>
                      ]}
                      rows={options}
                    />
                  </Card>
                  <div className="BC-Page__SearchAnalytics__SearchTermDetail__Pagination">
                    <div className="empty" />
                    {!_.isEmpty(searchTermDetail) && !searchTermDetailLoading && (
                      <Pagination
                        label={`${page}/${totalPage}`}
                        hasPrevious={page > 1}
                        onPrevious={this.previousPage}
                        hasNext={page < totalPage}
                        onNext={this.nextPage}
                      />
                    )}
                    <Button
                      primary
                      onClick={() => history.push(routes.analytics.searchesWithClick())}>
                      {i18n.translate('AdminHTML.Analytics.SearchAnalytics.add_merchandising_rule')}
                    </Button>
                  </div>
                </div>
              </div>
            </Layout.Section>
          </Layout>
        </Page>
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Analytics_Video']}
          onClose={() => this.toggleModal('youtubeModal')}
          title={i18n.translate('AdminHTML.Analytics.Analytics.youtubeModal_Title')}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getTopSearchTermDetail: (searchTerm, fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getSearchTermDetail(
        searchTerm,
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  sortData: (name, field, direction, fieldInsensitive = '') =>
    dispatch(AnalyticsActions.sortData(name, field, direction, fieldInsensitive)),
  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data)),
  getThemes: () => dispatch(themeSetupV3Action.getThemes(ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),
    searchTermDetail: _.get(state.analyticsV2, 'topSearchTermDetail', []),
    searchTermDetailLoading: _.get(state.analyticsV2, 'topSearchTermDetailLoading', false),
    searchTermDetailSuccess: _.get(state.analyticsV2, 'topSearchTermDetailSuccess', false),
    searchTermDetail_QueryExecutionId: _.get(
      state.analyticsV2,
      'topSearchTermDetailQueryExecutionId'
    ),
    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', [])
  };
};

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(SearchAnalyticsSearchTermDetail)
);
